import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useResource } from '../lib/useResource.js';
import { post, patch, del } from '../lib/api.js';
import { formatDateTime, formatDate, vehicleLabel } from '../lib/format.js';

/** Só a hora, que é o que cabe dentro do balão. */
const formatTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
import {
  Alert,
  Badge,
  Card,
  Empty,
  ErrorBox,
  Modal,
  PageHeader,
  useSubmit,
  useToast,
} from '../components/ui.js';
import { useSession } from '../lib/session.js';
import { useEhCelular } from '../lib/tela.js';
import { liberarSom, somLigado, definirSom, tocarAvisoDeMensagem } from '../lib/som.js';
import { ContatoDaConversa } from '../components/ContatoDaConversa.js';
import { Compositor } from '../components/Compositor.js';
import { IconeConferido, IconeEnviadoUm, IconeRelogio } from '../components/Icones.js';
import { BalaoDeAudio } from '../components/Audio.js';

interface Conversation {
  id: string;
  state: string;
  /** Contato identificado como robô: o assistente não responde. */
  robo_em: string | null;
  robo_motivo: string | null;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  handover_reason: string | null;
  customer_id: string | null;
  customer_name: string | null;
  nomeNoWhatsapp: string | null;
  external_phone: string | null;
  identificadorAnonimo: boolean;
  foto: string | null;
}

interface Message {
  id: string;
  conversation_id: string;
  attachment_id: string | null;
  provider_message_id: string | null;
  edited_at: string | null;
  direction: string;
  sender_kind: string;
  kind: string;
  body: string | null;
  transcript: string | null;
  status: string;
  origin: string;
  created_at: string;
  external_ts: string | null;
  /** Tipo da mídia recebida; `historico` quando veio do celular sem o arquivo. */
  media: { mimeType?: string; fileName?: string | null; historico?: boolean } | null;
}

interface ChannelStatus {
  provider: string;
  connected: boolean;
  detail: string;
  paused: boolean;
  pausedReason: string | null;
  queued: number;
  deliveryUnknown: number;
}

/**
 * De quanto em quanto tempo o atendimento se atualiza sozinho. Cinco segundos
 * é rápido o bastante para parecer ao vivo e devagar o bastante para não pesar:
 * a aba em segundo plano nem chega a pedir.
 */
const SEGUNDOS_DE_ATUALIZACAO = 5;

const ESTADO: Record<string, { label: string; tone: 'neutral' | 'attention' | 'critical' | 'ok' }> = {
  ai_active: { label: 'assistente ativo', tone: 'neutral' },
  waiting_human: { label: 'aguardando pessoa', tone: 'critical' },
  human_active: { label: 'em atendimento', tone: 'attention' },
  closed: { label: 'encerrada', tone: 'neutral' },
};

const MOTIVO: Record<string, string> = {
  sem_assistente_nesta_etapa: 'assistente ainda não ligado nesta instalação',
  possivel_descadastro: 'pode ser pedido para parar de receber — confirme',
  descadastro_registrado: 'pediu para parar de receber; já registrado',
  possivel_laco: 'muitas mensagens seguidas: pode ser outro robô',
  mensagem_enviada_do_celular: 'alguém respondeu pelo celular',
  contato_anterior: 'negociação em andamento com a equipe (resposta nas últimas 24 h)',
  orcamento_de_ia_esgotado: 'teto de gasto da IA atingido: responda pela equipe',
  assistente_desligado: 'assistente desligado',
};

export function Inbox() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useSession();
  const toast = useToast();
  const [recarga, setRecarga] = useState(0);
  const [busca, setBusca] = useState('');

  /**
   * O atendimento acompanha o que está acontecendo AGORA. Mensagem que só
   * aparece quando alguém lembra de apertar F5 não é atendimento.
   */
  const conversas = useResource<{ conversations: Conversation[] }>('/api/conversations', [recarga], {
    atualizarACada: SEGUNDOS_DE_ATUALIZACAO * 1000,
  });
  const canal = useResource<ChannelStatus>('/api/channel/status', [recarga], {
    // O estado do canal muda devagar; não precisa da mesma pressa.
    atualizarACada: 30_000,
  });
  /**
   * No computador, abrir a primeira conversa poupa um clique. No celular seria
   * o contrário do esperado: quem abre o WhatsApp vê a LISTA, não o último
   * atendimento de alguém. Aqui a conversa só abre quando a pessoa toca.
   */
  const ehCelular = useEhCelular();
  const selecionada = id ?? (ehCelular ? null : (conversas.data?.conversations[0]?.id ?? null));

  /**
   * Aviso sonoro de mensagem nova. Numa oficina, aviso só visual não chega:
   * o celular fica no bolso e a tela do balcão ninguém está olhando.
   */
  const [comSom, setComSom] = useState(somLigado);
  const ultimaEntrada = useRef<string | null>(null);
  useEffect(() => {
    const liberar = () => liberarSom();
    document.addEventListener('pointerdown', liberar, { once: true });
    return () => document.removeEventListener('pointerdown', liberar);
  }, []);
  useEffect(() => {
    const maisRecente = (conversas.data?.conversations ?? [])
      .map((c) => c.last_inbound_at)
      .filter((d): d is string => Boolean(d))
      .sort()
      .pop() ?? null;
    if (!maisRecente) return;
    // A primeira leitura só marca o ponto de partida: abrir a tela não toca.
    if (ultimaEntrada.current === null) {
      ultimaEntrada.current = maisRecente;
      return;
    }
    if (maisRecente > ultimaEntrada.current) {
      ultimaEntrada.current = maisRecente;
      if (comSom) tocarAvisoDeMensagem();
    }
  }, [conversas.data, comSom]);

  // O painel confere se chegou algo novo a cada 20 segundos. Quem estiver com
  // o aplicativo instalado também recebe o aviso no celular.
  useEffect(() => {
    const t = setInterval(() => setRecarga((r) => r + 1), 20_000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      {ehCelular && selecionada ? null : (
        <PageHeader
          title="Atendimento"
          description="Conversas do WhatsApp, com a ficha do cliente ao lado."
          actions={
            <button
              type="button"
              className="small"
              aria-pressed={comSom}
              title={comSom ? 'Desligar o som de mensagem nova' : 'Ligar o som de mensagem nova'}
              onClick={() => {
                const novo = !comSom;
                setComSom(novo);
                definirSom(novo);
                if (novo) {
                  liberarSom();
                  tocarAvisoDeMensagem();
                }
              }}
            >
              {comSom ? '🔔 Som ligado' : '🔕 Som desligado'}
            </button>
          }
        />
      )}

      {canal.data && !(ehCelular && selecionada) ? <AvisoDoCanal status={canal.data} /> : null}
      <ErrorBox error={conversas.error} />

      <div className={ehCelular ? 'stack' : 'grid cols-2'}>
        {ehCelular && selecionada ? null : (
        <Card title="Conversas" tight>
          {conversas.data && conversas.data.conversations.length === 0 ? (
            <Empty title="Nenhuma conversa ainda.">
              Assim que um cliente escrever para o número da loja, a conversa aparece aqui.
            </Empty>
          ) : (
            <>
            {/* Com o histórico do celular importado são centenas de conversas:
                achar o cliente pelo nome ou pelo número, como no WhatsApp. */}
            <input
              className="wa-busca"
              type="search"
              placeholder="Pesquisar nome ou número"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            <ul className="list">
              {(conversas.data?.conversations ?? []).filter((c) => combinaComBusca(c, busca)).map((conversa) => (
                <li
                  key={conversa.id}
                  className="conversa-item"
                  role="button"
                  tabIndex={0}
                  aria-current={conversa.id === selecionada ? 'true' : undefined}
                  onClick={() => navigate(`/atendimento/${conversa.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/atendimento/${conversa.id}`);
                    }
                  }}
                  style={
                    conversa.id === selecionada
                      ? { background: 'var(--gl-background)' }
                      : undefined
                  }
                >
                  {/* Desenho do WhatsApp: nome e hora na primeira linha,
                      prévia e situação na segunda. A hora embaixo, junto do
                      estado, fazia cada item ocupar três linhas. */}
                  <MiniFoto
                    foto={conversa.foto}
                    nome={conversa.customer_name ?? conversa.nomeNoWhatsapp ?? '?'}
                  />
                  <div className="list-main">
                    <div className="list-linha1">
                      <span className="list-title">
                        {conversa.customer_name ??
                          conversa.nomeNoWhatsapp ??
                          'Contato sem cadastro'}
                      </span>
                      <span className="list-when">
                        {formatDateTime(conversa.last_inbound_at)}
                      </span>
                    </div>
                    <div className="list-linha2">
                      <span className="list-sub">
                        {conversa.external_phone ??
                          (conversa.identificadorAnonimo ? 'número não revelado' : '')}
                        {conversa.external_phone || conversa.identificadorAnonimo ? ' · ' : ''}
                        {conversa.robo_em
                          ? `robô: ${conversa.robo_motivo ?? 'identificado'} — o assistente não responde`
                          : (MOTIVO[conversa.handover_reason ?? ''] ?? conversa.handover_reason ?? '—')}
                      </span>
                      <Badge tone={ESTADO[conversa.state]?.tone ?? 'neutral'}>
                        {conversa.robo_em ? 'robô' : (ESTADO[conversa.state]?.label ?? conversa.state)}
                      </Badge>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            </>
          )}
        </Card>
        )}

        {selecionada ? (
          <Conversa
            conversationId={selecionada}
            podeAssumir={can('conversations.takeover')}
            podeReiniciar={can('system.restart')}
            podeCadastrar={can('customers.write')}
            onVoltar={ehCelular ? () => navigate('/atendimento') : undefined}
            podeResponder={can('conversations.write')}
            envioBloqueado={canal.data?.paused ? motivoDoBloqueio(canal.data) : null}
            onMudou={() => {
              setRecarga((r) => r + 1);
              toast('Conversa atualizada.');
            }}
          />
        ) : null}
      </div>
    </>
  );
}

/** Foto pequena na lista. Sem foto, as iniciais — nunca um espaço em branco. */
/** Nome, nome no WhatsApp ou número — dígitos casam com ou sem máscara. */
function combinaComBusca(c: Conversation, busca: string): boolean {
  const termo = busca.trim().toLowerCase();
  if (!termo) return true;
  const nomes = [c.customer_name, c.nomeNoWhatsapp].filter(Boolean).join(' ').toLowerCase();
  if (nomes.includes(termo)) return true;
  const digitosDoTermo = termo.replace(/\D/g, '');
  return digitosDoTermo.length >= 3 && (c.external_phone ?? '').replace(/\D/g, '').includes(digitosDoTermo);
}

/**
 * Mídia sem arquivo no painel: a do histórico do celular (que veio só com o
 * tipo) e a que chegou antes de o painel guardar arquivos recebidos. O balão
 * diz o que é, em vez de ficar vazio.
 */
function rotuloDaMidia(m: Message): string | null {
  const mime = m.media?.mimeType ?? '';
  if (m.kind === 'audio' || mime.startsWith('audio/')) return '🎤 Áudio';
  if (mime.startsWith('video/')) return '🎬 Vídeo';
  if (mime === 'image/webp') return 'Figurinha';
  if (m.kind === 'image' || mime.startsWith('image/')) return '📷 Foto';
  if (m.kind === 'document' || mime) return `📄 ${m.media?.fileName ?? 'Documento'}`;
  return null;
}

function MiniFoto({ foto, nome }: { foto: string | null; nome: string }) {
  const [quebrou, setQuebrou] = useState(false);
  const estilo = {
    width: 34,
    height: 34,
    borderRadius: '50%',
    flexShrink: 0,
    objectFit: 'cover' as const,
  };
  if (foto && !quebrou) {
    return <img src={foto} alt="" onError={() => setQuebrou(true)} style={estilo} />;
  }
  return (
    <div
      aria-hidden
      style={{
        ...estilo,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--gl-accent)',
        color: 'var(--gl-accentStrong)',
        fontWeight: 700,
        fontSize: '0.8rem',
      }}
    >
      {nome.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'}
    </div>
  );
}

function AvisoDoCanal({ status }: { status: ChannelStatus }) {
  if (status.provider === 'none') {
    return (
      <Alert tone="info" title="Conexão de WhatsApp não configurada">
        Esta instalação ainda não tem conector ligado. Configure em Configurações › WhatsApp.
      </Alert>
    );
  }
  if (status.paused) {
    return (
      <Alert tone="warning" title="Envios pausados por decisão registrada">
        {status.pausedReason ?? 'sem motivo registrado'} — {status.queued} mensagens esperando na
        fila. Nada sai até alguém religar.
      </Alert>
    );
  }
  if (!status.connected) {
    return (
      <Alert tone="error" title="Conexão de WhatsApp fora do ar">
        {status.detail}. Os disparos param sozinhos e nada é reenviado sem decisão. A fila continua
        visível: {status.queued} mensagens esperando.
      </Alert>
    );
  }
  if (status.deliveryUnknown > 0) {
    return (
      <Alert tone="warning" title={`${status.deliveryUnknown} mensagens com entrega incerta`}>
        O provedor não confirmou o envio. Elas não são reenviadas sozinhas — confira em
        Configurações › WhatsApp.
      </Alert>
    );
  }
  return null;
}

interface FichaCliente {
  customer: { id: string; name: string; phone_e164: string | null } | null;
  vehicles: { id: string; make: string | null; model: string | null; model_year: number | null; plate: string | null }[];
  services: { id: string; performed_on: string; record_type: string }[];
  cycles: { id: string; purpose: string; next_send_at: string | null; status: string }[];
}

/**
 * Com o canal pausado nada sai — nem do assistente, nem do painel. A caixa de
 * texto some e no lugar fica o motivo, como o WhatsApp faz no grupo em que só
 * admin fala. Antes a caixa continuava ali: o Guel respondeu um cliente pelo
 * celular, a mensagem ficou parada na fila e ninguém percebeu (28/09/2026) —
 * o aviso de pausa só aparecia na lista, que no celular some com a conversa
 * aberta.
 */
function motivoDoBloqueio(status: ChannelStatus): string {
  return `Envios pausados: nada do que for escrito aqui chega ao cliente. Responda pelo celular da loja até o proprietário religar em Configurações › Sistema.${status.queued > 0 ? ` ${status.queued} na fila.` : ''}`;
}

function Conversa({
  conversationId,
  podeAssumir,
  podeResponder,
  podeReiniciar,
  podeCadastrar,
  envioBloqueado,
  onMudou,
  onVoltar,
}: {
  conversationId: string;
  podeAssumir: boolean;
  podeResponder: boolean;
  podeReiniciar: boolean;
  podeCadastrar: boolean;
  /** Motivo pelo qual nada sai agora; null quando o envio funciona. */
  envioBloqueado: string | null;
  onMudou: () => void;
  /** Só no celular: volta para a lista, como o ‹ do WhatsApp. */
  onVoltar?: (() => void) | undefined;
}) {
  const [recarga, setRecarga] = useState(0);
  const mensagens = useResource<{ messages: Message[] }>(
    `/api/conversations/${conversationId}/messages`,
    [conversationId, recarga],
    { atualizarACada: SEGUNDOS_DE_ATUALIZACAO * 1000 },
  );
  const conversas = useResource<{ conversations: Conversation[] }>('/api/conversations', [recarga], {
    atualizarACada: SEGUNDOS_DE_ATUALIZACAO * 1000,
  });
  const conversa = conversas.data?.conversations.find((c) => c.id === conversationId) ?? null;

  /**
   * Conversa aberta e na tela = cliente vê ✓✓ azul, como no WhatsApp. Uma vez
   * por mensagem nova do cliente, e só com a aba visível: painel esquecido
   * aberto em outra aba não "lê" nada.
   */
  const marcadaComoLida = useRef<string | null>(null);
  const ultimaDoCliente = [...(mensagens.data?.messages ?? [])].reverse().find((m) => m.direction === 'inbound');
  useEffect(() => {
    if (!ultimaDoCliente || marcadaComoLida.current === ultimaDoCliente.id) return;
    if (document.visibilityState !== 'visible') return;
    marcadaComoLida.current = ultimaDoCliente.id;
    void post(`/api/conversations/${conversationId}/lida`).catch(() => undefined);
  }, [conversationId, ultimaDoCliente?.id]);

  const veiculos = useResource<{ vehicles: FichaCliente['vehicles'] }>(
    conversa?.customer_id ? `/api/customers/${conversa.customer_id}/vehicles` : null,
  );
  const servicos = useResource<{ services: FichaCliente['services'] }>(
    conversa?.customer_id ? `/api/customers/${conversa.customer_id}/services` : null,
  );

  const assumir = useSubmit(async () => {
    await post(`/api/conversations/${conversationId}/takeover`, {});
    setRecarga((r) => r + 1);
    onMudou();
  });

  /**
   * Devolve a conversa ao assistente. Existe porque assumir sem caminho de
   * volta é uma porta de mão única: a pessoa resolve o que tinha que resolver e
   * a conversa fica presa nela para sempre — e o cliente perde o atendimento
   * automático fora do expediente.
   */
  const devolver = useSubmit(async () => {
    await post(`/api/conversations/${conversationId}/release`, {
      reason: 'devolvida ao assistente pelo painel',
    });
    setRecarga((r) => r + 1);
    onMudou();
  });

  const naoERobo = useSubmit(async () => {
    await post(`/api/conversations/${conversationId}/nao-e-robo`, {});
    setRecarga((r) => r + 1);
    onMudou();
  });

  const encerrar = useSubmit(async () => {
    await post(`/api/conversations/${conversationId}/close`, { reason: 'atendimento concluído' });
    setRecarga((r) => r + 1);
    onMudou();
  });

  /**
   * Reinicia a conversa para repetir um teste com o mesmo celular: APAGA as
   * mensagens daquele chat e zera a ficha que a IA montou, o encaminhamento e
   * os contadores. Cliente, veículos, serviços e agendamentos não são tocados.
   */
  const [confirmandoReinicio, setConfirmandoReinicio] = useState(false);
  const reiniciar = useSubmit(async () => {
    await post(`/api/sistema/conversas/${conversationId}/reiniciar`, {
      motivo: 'teste do atendimento pelo painel',
    });
    setConfirmandoReinicio(false);
    setRecarga((r) => r + 1);
    onMudou();
  });

  /**
   * A conversa abre embaixo, na mensagem mais nova — como todo aplicativo de
   * mensagem. Abrir no topo obriga a pessoa a rolar até o fim toda vez, e o que
   * interessa num atendimento é sempre a última linha.
   *
   * Só rola sozinho quando a pessoa JÁ estava no fim: se ela subiu para reler
   * algo, uma mensagem nova não pode arrancar a tela da mão dela.
   */
  const [editando, setEditando] = useState<Message | null>(null);
  const [apagando, setApagando] = useState<{ m: Message; alcance: 'todos' | 'aqui' } | null>(null);
  /**
   * Apagar para todos some do celular do cliente e não tem desfazer — por isso
   * pergunta antes. "Só aqui" é reversível na prática (a mensagem continua no
   * banco e na auditoria), então vai direto.
   */
  async function apagar(m: Message, alcance: 'todos' | 'aqui') {
    await del(`/api/conversations/${conversationId}/messages/${m.id}?alcance=${alcance}`);
    setApagando(null);
    setRecarga((r) => r + 1);
  }

  const areaMensagens = useRef<HTMLDivElement>(null);
  const quantasMensagens = mensagens.data?.messages.length ?? 0;
  useEffect(() => {
    const el = areaMensagens.current;
    if (!el) return;
    const estavaNoFim = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (estavaNoFim || el.scrollTop === 0) el.scrollTop = el.scrollHeight;
  }, [quantasMensagens, conversationId]);

  // O teclado abrindo encolhe a área: sem isto a última mensagem fica atrás dele.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const aoMudar = () => {
      const el = areaMensagens.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    vv.addEventListener('resize', aoMudar);
    return () => vv.removeEventListener('resize', aoMudar);
  }, []);

  const ultimoServico = servicos.data?.services.find((s) => s.record_type === 'performed');

  return (
    <>
      <div className="wa-tela">
      {/* Cabeçalho da conversa: foto, nome, telefone e as ações num menu.
          Três botões grandes empurravam o telefone para fora e o quebravam
          letra por letra — foi o que a tela do proprietário mostrou. */}
      <ContatoDaConversa
        conversationId={conversationId}
        podeCadastrar={podeCadastrar}
        onVoltar={onVoltar}
        acaoPrincipal={
          !podeAssumir ? null : conversa?.robo_em ? (
            // Robô identificado pelo jeito que escreve. Se for engano, uma
            // pessoa desfaz e o assistente volta a atender o contato.
            <button
              type="button"
              className="wa-cadastrar devolver"
              disabled={naoERobo.saving}
              onClick={() => void naoERobo.submit()}
              title={conversa.robo_motivo ?? undefined}
            >
              {naoERobo.saving ? 'Liberando…' : 'Não é robô'}
            </button>
          ) : conversa?.state === 'human_active' ? (
            // Com a conversa na mão de uma pessoa, a ação do momento é a
            // oposta: devolver ao assistente.
            <button
              type="button"
              className="wa-cadastrar devolver"
              disabled={devolver.saving}
              onClick={() => void devolver.submit()}
            >
              {devolver.saving ? 'Devolvendo…' : 'Devolver à IA'}
            </button>
          ) : (
            <button
              type="button"
              className="wa-cadastrar"
              disabled={assumir.saving}
              onClick={() => void assumir.submit()}
            >
              {assumir.saving ? 'Assumindo…' : 'Assumir'}
            </button>
          )
        }
        acoes={
          <>
            {conversa?.state !== 'closed' ? (
              <button type="button" onClick={() => void encerrar.submit()}>
                Encerrar conversa
              </button>
            ) : null}
            {podeReiniciar ? (
              <button type="button" onClick={() => setConfirmandoReinicio(true)}>
                Apagar e reiniciar
              </button>
            ) : null}
          </>
        }
        onCadastrou={() => {
          setRecarga((r) => r + 1);
          onMudou();
        }}
      />

        <ErrorBox
          error={
            assumir.error ?? devolver.error ?? naoERobo.error ?? encerrar.error ?? reiniciar.error ?? mensagens.error
          }
        />

        {confirmandoReinicio ? (
          <Alert tone="warning" title="Apagar esta conversa?">
            <p style={{ margin: '6px 0' }}>
              <strong>Todas as mensagens deste chat serão apagadas</strong> e a próxima mensagem
              deste número será tratada como se fosse a primeira: some também a ficha que a IA
              montou (veículo e pedido), o encaminhamento e os contadores. Não tem desfazer.
            </p>
            <p style={{ margin: '6px 0' }} className="hint">
              O cadastro do cliente, veículos, serviços, agendamentos e lembretes não são tocados.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="small primary"
                disabled={reiniciar.saving}
                onClick={() => void reiniciar.submit()}
              >
                Sim, apagar e reiniciar
              </button>
              <button type="button" className="small" onClick={() => setConfirmandoReinicio(false)}>
                Cancelar
              </button>
            </div>
          </Alert>
        ) : null}

        {conversa?.state === 'waiting_human' ? (
          <Alert tone="warning" title="Cliente aguardando pessoa">
            Assumir interrompe qualquer automação para este contato na hora.
          </Alert>
        ) : null}

        {conversa?.state === 'human_active' ? (
          <Alert tone="info" title="Você está atendendo — o assistente está parado">
            Ele não responde nada neste contato enquanto a conversa estiver com você. Toque em
            <strong> Devolver à IA</strong> quando terminar.
          </Alert>
        ) : null}

        <div className="wa-mensagens" ref={areaMensagens}>
          {(mensagens.data?.messages ?? []).length === 0 ? (
            <p className="wa-vazio">
              Nenhuma mensagem ainda. Escreva abaixo para começar.
            </p>
          ) : (
            (mensagens.data?.messages ?? []).map((m, i, todas) => {
              const quando = m.external_ts ?? m.created_at;
              const anterior = i > 0 ? todas[i - 1] : null;
              const mudouODia =
                !anterior ||
                rotuloDoDia(quando) !== rotuloDoDia(anterior.external_ts ?? anterior.created_at);
              // Primeiro da sequência = muda o lado, ou é o primeiro do dia.
              const primeiro = mudouODia || anterior?.direction !== m.direction;
              return (
                <Fragment key={m.id}>
                  {mudouODia ? <div className="wa-dia">{rotuloDoDia(quando)}</div> : null}
                  <Balao
                    mensagem={m}
                    primeiro={primeiro}
                    foto={conversa?.foto ?? null}
                    onEditar={setEditando}
                    onApagar={(m, alcance) =>
                      alcance === 'todos'
                        ? setApagando({ m, alcance })
                        : void apagar(m, 'aqui')
                    }
                  />
                </Fragment>
              );
            })
          )}
        </div>

        {apagando ? (
          <Modal title="Apagar para todos?" onClose={() => setApagando(null)}>
            <p>
              A mensagem some também do celular de quem recebeu. <strong>Não tem desfazer.</strong>
            </p>
            <p className="hint">
              Ela continua guardada aqui no sistema e na auditoria — some da conversa, não do
              registro.
            </p>
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="button button-primary"
                onClick={() => void apagar(apagando.m, 'todos')}
              >
                Apagar para todos
              </button>
              <button type="button" onClick={() => setApagando(null)}>
                Cancelar
              </button>
            </div>
          </Modal>
        ) : null}

        {editando ? (
          <EdicaoDeMensagem
            conversationId={conversationId}
            mensagem={editando}
            onFechar={() => setEditando(null)}
            onPronto={() => {
              setEditando(null);
              setRecarga((r) => r + 1);
            }}
          />
        ) : null}

        {podeResponder && conversa?.state !== 'closed' && envioBloqueado ? (
          <div className="wa-barra wa-bloqueado" role="status">
            {envioBloqueado}
          </div>
        ) : podeResponder && conversa?.state !== 'closed' ? (
          <Compositor
            conversationId={conversationId}
            nomeDoCliente={conversa?.customer_name ?? conversa?.nomeNoWhatsapp ?? null}
            onEnviou={() => {
              setRecarga((r) => r + 1);
              onMudou();
            }}
          />
        ) : null}
      </div>

      <div className="wa-ficha">
      <Card title="Ficha do cliente">
        {conversa?.customer_id ? (
          <>
            <p>
              <strong>{conversa.customer_name}</strong>
            </p>
            <dl style={{ margin: 0 }}>
              <Linha rotulo="Veículo">
                {veiculos.data?.vehicles.length
                  ? veiculos.data.vehicles.map((v) => vehicleLabel(v)).join(' · ')
                  : 'sem veículo cadastrado'}
              </Linha>
              <Linha rotulo="Último serviço">
                {ultimoServico ? formatDate(ultimoServico.performed_on) : 'nenhum registrado aqui'}
              </Linha>
            </dl>
            <div className="row" style={{ marginTop: 12 }}>
              <a className="button small" href={`/clientes/${conversa.customer_id}`}>
                Abrir ficha completa
              </a>
              <a className="button small" href={`/servicos/novo?cliente=${conversa.customer_id}`}>
                Registrar serviço
              </a>
            </div>
          </>
        ) : (
          <Alert tone="info" title="Contato sem cadastro">
            Este número não bate com nenhum cliente cadastrado. Cadastre o cliente para vincular a
            conversa ao histórico — o sistema não junta cadastros por conta própria.
          </Alert>
        )}
      </Card>
      </div>
    </>
  );
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0' }}>
      <dt style={{ minWidth: 120, fontWeight: 600 }}>{rotulo}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  );
}

/**
 * Balão de mensagem no desenho do WhatsApp: cantos de 8 px, cauda só no
 * primeiro de uma sequência do mesmo lado, e a hora DENTRO do balão, embaixo à
 * direita, com as confirmações em seguida.
 *
 * A hora dentro não é enfeite: é o que deixa a lista compacta. Hora numa linha
 * separada dobra a altura de cada mensagem curta, e conversa de atendimento é
 * feita de mensagem curta.
 */
function Balao({
  mensagem,
  primeiro,
  foto,
  onEditar,
  onApagar,
}: {
  mensagem: Message;
  primeiro: boolean;
  foto?: string | null;
  onEditar?: (m: Message) => void;
  onApagar?: (m: Message, alcance: 'todos' | 'aqui') => void;
}) {
  const doCliente = mensagem.direction === 'inbound';
  /**
   * No áudio a transcrição vai DENTRO do balão do áudio, embaixo da onda —
   * como no WhatsApp. Usá-la como texto do balão faria o recado aparecer duas
   * vezes e esconderia o tocador.
   */
  const ehAudio = mensagem.kind === 'audio';
  const conteudo = ehAudio ? mensagem.body : (mensagem.body ?? mensagem.transcript ?? null);
  const midia = mensagem.attachment_id
    ? `/api/conversations/${mensagem.conversation_id}/anexos/${mensagem.attachment_id}`
    : null;

  const [menuAberto, setMenuAberto] = useState(false);
  const toqueLongo = useRef<number | null>(null);

  /**
   * Toque longo abre o NOSSO menu. Sem isto o navegador abre o dele —
   * "Copiar, Pesquisar, Traduzir" —, que não tem Editar e não serve para nada
   * num atendimento.
   */
  function comecarToque() {
    toqueLongo.current = window.setTimeout(() => setMenuAberto(true), 450);
  }
  function soltarToque() {
    if (toqueLongo.current) window.clearTimeout(toqueLongo.current);
    toqueLongo.current = null;
  }

  return (
    <div
      className={`wa-balao ${doCliente ? 'entrada' : 'saida'} ${primeiro ? 'primeiro' : ''}`}
      onPointerDown={comecarToque}
      onPointerUp={soltarToque}
      onPointerLeave={soltarToque}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenuAberto(true);
      }}
    >
      {midia && mensagem.kind === 'image' ? (
        <img className="anexo" src={midia} alt={conteudo ?? 'Foto enviada'} />
      ) : null}
      {midia && ehAudio ? (
        <BalaoDeAudio
          url={midia}
          semente={mensagem.id}
          foto={doCliente ? foto : null}
          transcricao={mensagem.transcript}
        />
      ) : null}
      {midia && mensagem.kind === 'document' ? (
        <a href={midia} target="_blank" rel="noreferrer">
          Abrir documento
        </a>
      ) : null}
      {!midia && rotuloDaMidia(mensagem) ? (
        <em className="wa-midia-ausente">
          {rotuloDaMidia(mensagem)}
          {mensagem.media?.historico ? ' · abrir no celular' : ''}
        </em>
      ) : null}
      {!midia && ehAudio && mensagem.transcript ? <span className="wa-transcricao">{mensagem.transcript}</span> : null}
      {mensagem.kind === 'location' && !conteudo ? (doCliente ? '📍 Localização' : 'Localização da loja') : null}
      {mensagem.kind === 'contact' && !conteudo ? 'Cartão de contato' : null}
      {conteudo}
      <span className="wa-rodape">
        {mensagem.edited_at ? <em className="wa-editada">editada</em> : null}
        {formatTime(mensagem.external_ts ?? mensagem.created_at)}
        {!doCliente ? <Confirmacao mensagem={mensagem} /> : null}
      </span>

      {menuAberto ? (
        <MenuDaMensagem
          mensagem={mensagem}
          onFechar={() => setMenuAberto(false)}
          onEditar={() => {
            setMenuAberto(false);
            onEditar?.(mensagem);
          }}
          onApagar={(alcance) => {
            setMenuAberto(false);
            onApagar?.(mensagem, alcance);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Menu do toque longo. Editar aparece só quando o WhatsApp aceitaria: mensagem
 * nossa, de texto, já enviada e dentro dos 14 minutos. Oferecer um botão que
 * vai dar erro é pior do que não oferecer.
 */
/**
 * Caixa de edição. Mostra o texto atual para a pessoa corrigir, e avisa que o
 * WhatsApp marca a mensagem como editada para quem recebeu — ninguém deve
 * descobrir isso depois, pelo cliente.
 */
function EdicaoDeMensagem({
  conversationId,
  mensagem,
  onFechar,
  onPronto,
}: {
  conversationId: string;
  mensagem: Message;
  onFechar: () => void;
  onPronto: () => void;
}) {
  const [texto, setTexto] = useState(mensagem.body ?? '');
  const salvar = useSubmit(async () => {
    await patch(`/api/conversations/${conversationId}/messages/${mensagem.id}`, {
      body: texto.trim(),
    });
    onPronto();
  });

  return (
    <Modal title="Editar mensagem" onClose={onFechar}>
      <ErrorBox error={salvar.error} />
      <textarea
        value={texto}
        rows={4}
        autoFocus
        onChange={(e) => setTexto(e.target.value)}
      />
      <p className="hint">
        O WhatsApp mostra “editada” para quem recebeu, e guarda aqui o texto anterior.
      </p>
      <button
        type="button"
        className="button button-primary"
        disabled={salvar.saving || texto.trim().length === 0 || texto.trim() === mensagem.body}
        onClick={() => void salvar.submit()}
      >
        {salvar.saving ? 'Salvando…' : 'Salvar'}
      </button>
    </Modal>
  );
}

function MenuDaMensagem({
  mensagem,
  onFechar,
  onEditar,
  onApagar,
}: {
  mensagem: Message;
  onFechar: () => void;
  onEditar: () => void;
  onApagar: (alcance: 'todos' | 'aqui') => void;
}) {
  const minutos =
    (Date.now() - new Date(mensagem.created_at).getTime()) / 60_000;
  const podeEditar =
    mensagem.direction === 'outbound' &&
    mensagem.kind === 'text' &&
    Boolean(mensagem.provider_message_id) &&
    minutos <= 14;

  /* Apagar para todos só vale para o que a loja mandou e já saiu — o WhatsApp
     não deixa apagar mensagem de outra pessoa do celular dela. */
  const podeApagarParaTodos =
    mensagem.direction === 'outbound' && Boolean(mensagem.provider_message_id);

  return (
    <>
      <div className="wa-fundo" onClick={onFechar} />
      <div className="wa-menu-msg" role="menu">
        {podeEditar ? (
          <button type="button" onClick={onEditar}>
            Editar
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard?.writeText(mensagem.body ?? mensagem.transcript ?? '');
            onFechar();
          }}
        >
          Copiar
        </button>
        {podeApagarParaTodos ? (
          <button type="button" className="perigo" onClick={() => onApagar('todos')}>
            Apagar para todos
          </button>
        ) : null}
        <button type="button" onClick={() => onApagar('aqui')}>
          Apagar só aqui
        </button>

        {!podeEditar && mensagem.direction === 'outbound' && mensagem.kind === 'text' ? (
          <p className="hint" style={{ padding: '6px 12px', margin: 0 }}>
            O WhatsApp só deixa editar nos primeiros 14 minutos.
          </p>
        ) : null}
        {!podeApagarParaTodos ? (
          <p className="hint" style={{ padding: '6px 12px', margin: 0 }}>
            “Só aqui” some do painel; no celular do cliente continua.
          </p>
        ) : null}
      </div>
    </>
  );
}

/** Os tiques do WhatsApp: relógio na fila, um tique enviado, dois entregue,
 *  dois azuis lida. Falha vira aviso vermelho — silêncio ali engana. */
function Confirmacao({ mensagem }: { mensagem: Message }) {
  if (mensagem.origin === 'provider') return <span title="enviada pelo celular">📱</span>;
  switch (mensagem.status) {
    case 'queued':
      return <IconeRelogio size={13} />;
    case 'accepted':
      return <IconeEnviadoUm size={15} />;
    case 'delivered':
      return <IconeConferido size={15} />;
    case 'read':
      return <IconeConferido size={15} className="lida" />;
    case 'failed':
      return <span style={{ color: '#d93025', fontWeight: 700 }} title="falhou">!</span>;
    default:
      return <IconeRelogio size={13} />;
  }
}

/** Separador de dia, como no WhatsApp: "Hoje", "Ontem" ou a data. */
function rotuloDoDia(iso: string, agora = new Date()): string {
  const d = new Date(iso);
  const dia = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  const ontem = new Date(agora);
  ontem.setDate(ontem.getDate() - 1);
  if (dia(d) === dia(agora)) return 'Hoje';
  if (dia(d) === dia(ontem)) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

